import { useMemo, useState } from 'react';
import { ProyectoMock } from '../../../mocks/proyectos';

interface Props {
  proyectos: ProyectoMock[];
  evaluadorActivo?: string;
  onSelectProject?: (codigo: string, evaluadorNombre: string) => void;
}

interface EvaluadorResumen {
  nombre: string;
  proyectosEvaluados: {
    codigo: string;
    titulo: string;
    promedio: number | null;
    recomendacion: string;
    fecha: string;
  }[];
}

function calcularPromedio(scores: Record<string, string>): number | null {
  const vals = Object.values(scores).map(v => parseInt(v, 10)).filter(v => !isNaN(v));
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function getPromedioColor(avg: number): string {
  if (avg >= 4) return 'text-emerald-600';
  if (avg >= 3) return 'text-teal-600';
  if (avg >= 2) return 'text-amber-600';
  return 'text-red-500';
}

function getPromedioBadge(avg: number): string {
  if (avg >= 4) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (avg >= 3) return 'bg-teal-50 text-teal-700 border-teal-200';
  if (avg >= 2) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-red-50 text-red-600 border-red-200';
}

function getInitials(nombre: string): string {
  return nombre
    .split(' ')
    .filter(w => !['dr.', 'dra.', 'lic.', 'mg.', 'phd.', 'ing.', 'prof.', 'est.'].includes(w.toLowerCase()))
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('');
}

export default function EvaluadoresMemoria({ proyectos, evaluadorActivo, onSelectProject }: Props) {
  const [expandedEvaluador, setExpandedEvaluador] = useState<string | null>(null);

  const evaluadores = useMemo<EvaluadorResumen[]>(() => {
    const map = new Map<string, EvaluadorResumen>();
    for (const p of proyectos) {
      for (const ev of p.evaluaciones ?? []) {
        const nombre = ev.evaluador.trim();
        if (!nombre) continue;
        if (!map.has(nombre)) {
          map.set(nombre, { nombre, proyectosEvaluados: [] });
        }
        map.get(nombre)!.proyectosEvaluados.push({
          codigo: p.codigo,
          titulo: p.titulo,
          promedio: calcularPromedio(ev.scores),
          recomendacion: ev.recomendacion || '—',
          fecha: ev.fecha,
        });
      }
    }
    return Array.from(map.values()).sort(
      (a, b) => b.proyectosEvaluados.length - a.proyectosEvaluados.length,
    );
  }, [proyectos]);

  if (evaluadores.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 flex items-center justify-center bg-teal-600 rounded">
            <i className="ri-group-line text-white text-sm" />
          </div>
          <h3 className="text-sm font-bold text-slate-700">Evaluadores</h3>
        </div>
        <div className="text-center py-8 text-slate-400">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
            <i className="ri-user-search-line text-3xl" />
          </div>
          <p className="text-xs font-medium text-slate-500">Ningún evaluador aún</p>
          <p className="text-xs text-slate-400 mt-1">Aquí aparecerán cuando se guarden evaluaciones</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center bg-teal-600 rounded">
            <i className="ri-group-line text-white text-sm" />
          </div>
          <h3 className="text-sm font-bold text-slate-700">Evaluadores</h3>
        </div>
        <span className="text-xs font-semibold bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">
          {evaluadores.length}
        </span>
      </div>

      <div className="divide-y divide-slate-100 max-h-[520px] overflow-y-auto">
        {evaluadores.map(ev => {
          const isActivo = evaluadorActivo?.toLowerCase() === ev.nombre.toLowerCase();
          const isExpanded = expandedEvaluador === ev.nombre;
          const totalProyectos = ev.proyectosEvaluados.length;
          const promedios = ev.proyectosEvaluados.map(p => p.promedio).filter((v): v is number => v !== null);
          const avgGlobal = promedios.length > 0 ? promedios.reduce((a, b) => a + b, 0) / promedios.length : null;

          return (
            <div key={ev.nombre} className={isActivo ? 'bg-teal-50/60' : ''}>
              <button
                onClick={() => setExpandedEvaluador(isExpanded ? null : ev.nombre)}
                className="cursor-pointer w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {/* Avatar */}
                  <div className={`w-9 h-9 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${isActivo ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
                    {getInitials(ev.nombre) || <i className="ri-user-line text-sm" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-xs font-semibold text-slate-800 truncate">{ev.nombre}</p>
                      {isActivo && (
                        <span className="text-xs bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">
                          activo
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-slate-500">
                        {totalProyectos} proyecto{totalProyectos !== 1 ? 's' : ''}
                      </span>
                      {avgGlobal !== null && (
                        <span className={`text-xs font-semibold ${getPromedioColor(avgGlobal)}`}>
                          · prom. {avgGlobal.toFixed(1)}/5
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="w-4 h-4 flex items-center justify-center text-slate-400 flex-shrink-0">
                    <i className={isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
                  </div>
                </div>
              </button>

              {/* Expanded: list of evaluated projects */}
              {isExpanded && (
                <div className="px-4 pb-3 space-y-1.5">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    Proyectos evaluados
                    {onSelectProject && (
                      <span className="text-slate-300 font-normal normal-case">· clic para ver evaluación</span>
                    )}
                  </p>
                  {ev.proyectosEvaluados.map(p => (
                    <button
                      key={p.codigo}
                      onClick={() => onSelectProject?.(p.codigo, ev.nombre)}
                      className={`w-full text-left flex items-start gap-2.5 rounded-md px-3 py-2 border transition-all ${
                        onSelectProject
                          ? 'bg-slate-50 hover:bg-teal-50 hover:border-teal-300 border-transparent cursor-pointer'
                          : 'bg-slate-50 border-transparent cursor-default'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                          <span className="font-mono text-xs font-bold text-teal-700">{p.codigo}</span>
                          {p.promedio !== null && (
                            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full border ${getPromedioBadge(p.promedio)}`}>
                              {p.promedio.toFixed(1)}/5
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-600 leading-snug line-clamp-2">{p.titulo}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-slate-400">{p.fecha}</span>
                          {p.recomendacion !== '—' && (
                            <span className="text-xs text-slate-500">· {p.recomendacion}</span>
                          )}
                        </div>
                      </div>
                      {onSelectProject && (
                        <span className="w-4 h-4 flex items-center justify-center text-slate-300 flex-shrink-0 mt-0.5">
                          <i className="ri-arrow-right-s-line text-sm" />
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary footer */}
      <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100">
        <p className="text-xs text-slate-500 text-center">
          <strong>{evaluadores.reduce((sum, e) => sum + e.proyectosEvaluados.length, 0)}</strong> evaluaciones registradas en total
        </p>
      </div>
    </div>
  );
}
